import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Clock, CheckCircle2, XCircle, PauseCircle, Building, User as UserIcon,
  ShieldAlert, History, Archive,
} from "lucide-react";
import Layout from "@/components/Layout";
import RemarksModal from "@/components/RemarksModal";
import AmendDecisionModal from "@/components/AmendDecisionModal";
import { FORM_TYPE_LABEL } from "@/components/FormDetails";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/contexts/AuthContext";
import { isSuperAdmin } from "@/lib/roles";
import api from "@/lib/api";

function tatChip(deadline, status, current_stage) {
  if (!deadline || ["completed", "rejected", "on_hold"].includes(status)) return null;
  if (!["hod_review", "admin1_review", "admin2_review"].includes(current_stage)) return null;
  const remainingH = (new Date(deadline) - new Date()) / 3600000;
  let tone = "bg-emerald-100 text-emerald-800 border-emerald-300";
  let text = `${remainingH.toFixed(1)}h left`;
  if (remainingH <= 0) {
    tone = "bg-[#fcebe9] text-[#b3261e] border-[#b3261e]/40";
    text = `TAT breached · ${Math.abs(remainingH).toFixed(1)}h ago`;
  } else if (remainingH < 1) {
    tone = "bg-[#fcebe9] text-[#b3261e] border-[#b3261e]/40";
  } else if (remainingH <= 4) {
    tone = "bg-amber-100 text-amber-800 border-amber-300";
  }
  return { tone, text };
}

function tatLine(deadline) {
  const remainingH = (new Date(deadline) - new Date()) / 3600000;
  const ts = new Date(deadline).toLocaleString();
  const tone = remainingH <= 0 ? "text-[#b3261e]" : remainingH <= 4 ? "text-[#b48a45]" : "text-[#2d6b6b]";
  const label = remainingH <= 0
    ? `TAT breached ${Math.abs(remainingH).toFixed(1)}h ago`
    : `${remainingH.toFixed(1)} working-hours remaining`;
  return <span className={tone}>TAT deadline: {ts} · {label}</span>;
}

function summaryLine(r) {
  const fd = r.form_data || {};
  const bits = [];
  if (r.form_type === "material") bits.push(fd.materialCategory || "", fd.purposeOfUtility);
  else if (r.form_type === "event") bits.push(fd.eventName, fd.eventDate);
  else if (r.form_type === "transportation") bits.push(fd.tripType, fd.purposeOfTravel);
  else if (r.form_type === "fnb") bits.push(fd.purposeOfRequirement, fd.location);
  else if (r.form_type === "travel") bits.push(fd.purposeOfTravel, `${fd.city || ""} ${fd.country || ""}`);
  else if (r.form_type === "hotel") bits.push(fd.hotelName, fd.purposeOfBooking);
  return bits.filter(Boolean).join(" · ").slice(0, 160);
}

function ageWorkingHours(submitted) {
  const mins = (new Date() - new Date(submitted)) / 60000;
  return Math.max(0, (mins / 60)).toFixed(1);
}

// Latest action performed by this user (excluding "submitted")
function myLastAction(r, userId) {
  const acts = (r.audit_trail || []).filter(
    (a) => a.actor_id === userId &&
           ["approve", "on_hold", "reject", "super_admin_edit",
            "super_admin_reassignment", "super_admin_stage_override",
            "super_admin_delete", "super_admin_restore"].includes(a.action),
  );
  return acts.length ? acts[acts.length - 1] : null;
}

const ACTION_BADGE = {
  approve: { text: "You approved", tone: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  on_hold: { text: "You held", tone: "bg-amber-100 text-amber-800 border-amber-300" },
  reject:  { text: "You rejected", tone: "bg-[#fcebe9] text-[#b3261e] border-[#b3261e]/40" },
};

export default function HodDashboard() {
  const { user } = useAuth();
  const superView = isSuperAdmin(user);
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState([]);
  const [actioned, setActioned] = useState([]);
  const [historical, setHistorical] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, req: null, action: null });
  const [amend, setAmend] = useState({ open: false, req: null, previousAction: null, previousRemarks: "" });
  const [recentFilter, setRecentFilter] = useState("all");

  const load = () => {
    setLoading(true);
    const pendingUrl = superView ? "/requisitions?filter=pending_hod" : "/requisitions?filter=pending_hod_mine";
    const actionedUrl = "/requisitions?filter=actioned_by_me";
    const historyUrl = "/requisitions?filter=my_assignments_historical";
    Promise.all([api.get(pendingUrl), api.get(actionedUrl), api.get(historyUrl)])
      .then(([p, a, h]) => {
        setPending(p.data || []);
        setActioned(a.data || []);
        setHistorical(h.data || []);
      })
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const onActionDone = () => { load(); };

  const stats = useMemo(() => ({
    pending: pending.length,
    actioned: actioned.length,
    historical: historical.length,
  }), [pending, actioned, historical]);

  const filteredActioned = actioned.filter((r) => {
    if (recentFilter === "all") return true;
    const act = myLastAction(r, user.id);
    return act && act.action === recentFilter;
  });

  const rows = tab === "pending" ? pending : tab === "actioned" ? filteredActioned : historical;

  return (
    <Layout>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#7a1f2b] font-semibold">HOD Console</div>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#1f2320] leading-tight" data-testid="hod-heading">
            HOD Approval Queue
          </h1>
          <div className="mt-2 text-sm text-[#4a524c] flex items-center gap-2 flex-wrap">
            {superView ? (
              <>
                <ShieldAlert size={14} className="text-[#7a1f2b]" />
                <span className="font-medium">Super Admin View — showing all HOD queues</span>
              </>
            ) : (
              <>
                <UserIcon size={14} className="text-[#7a1f2b]" />
                <span>showing pending items for {user?.name}</span>
                {(user?.departments || []).map((d) => (
                  <span key={d} className="badge bg-[#f4e9eb] text-[#7a1f2b] border-[#7a1f2b]/40">{d}</span>
                ))}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-[#e2d7c3] mb-4 flex items-center gap-1 overflow-x-auto" data-testid="hod-tabs">
        <TabBtn active={tab === "pending"} onClick={() => setTab("pending")} testid="hod-tab-pending"
                icon={<CheckCircle2 size={14} />} label="Pending My Approval" count={stats.pending} tone="maroon" />
        <TabBtn active={tab === "actioned"} onClick={() => setTab("actioned")} testid="hod-tab-actioned"
                icon={<History size={14} />} label="Actioned by Me" count={stats.actioned} tone="teal" />
        <TabBtn active={tab === "historical"} onClick={() => setTab("historical")} testid="hod-tab-historical"
                icon={<Archive size={14} />} label="All My Assignments" count={stats.historical} tone="gold" />
      </div>

      {/* Filter chips for Actioned tab */}
      {tab === "actioned" && (
        <div className="flex flex-wrap gap-1 mb-3" data-testid="hod-actioned-filters">
          {["all", "approve", "on_hold", "reject"].map((f) => (
            <button key={f} onClick={() => setRecentFilter(f)}
                    className={`px-3 py-1 rounded-full text-[11px] uppercase tracking-widest ${
                      recentFilter === f ? "bg-[#7a1f2b] text-white" : "bg-[#faf5eb] text-[#4a524c] hover:bg-[#f4e9eb]"
                    }`}
                    data-testid={`hod-actioned-filter-${f}`}>
              {f === "on_hold" ? "on hold" : f}
            </button>
          ))}
        </div>
      )}

      {loading && <div className="p-6 text-[#6e7770]">Loading…</div>}

      {!loading && rows.length === 0 && (
        <div className="sheet p-10 text-center text-[#6e7770]" data-testid="hod-empty">
          <div className="mx-auto mb-3 w-10 h-10 rounded-full bg-[#faf5eb] border border-[#e2d7c3] flex items-center justify-center">
            <UserIcon size={18} className="text-[#7a1f2b]" />
          </div>
          <div className="font-serif text-lg text-[#7a1f2b] mb-1">
            {tab === "pending"     && "You have no pending items."}
            {tab === "actioned"    && "You haven't actioned anything yet."}
            {tab === "historical"  && "No requisitions have ever been assigned to you."}
          </div>
          <div>When requisitions arrive, they&apos;ll appear here.</div>
          {superView && (
            <div className="mt-3 text-xs text-[#6e7770]" data-testid="hod-empty-super-hint">
              Super Admin can view all queues by toggling the role tabs above.
            </div>
          )}
        </div>
      )}

      <div className="space-y-3">
        {rows.map((r) => (
          <HodCard key={r.id} req={r} tab={tab} userId={user.id} superView={superView}
                   openModal={(action) => setModal({ open: true, req: r, action })}
                   openAmend={(prev) => setAmend({
                     open: true, req: r,
                     previousAction: prev?.action,
                     previousRemarks: prev?.remarks || "",
                   })} />
        ))}
      </div>

      <RemarksModal
        open={modal.open}
        onClose={() => setModal({ open: false, req: null, action: null })}
        onDone={onActionDone}
        requisition={modal.req}
        action={modal.action}
        accent="maroon"
      />
      <AmendDecisionModal
        open={amend.open}
        requisition={amend.req}
        previousAction={amend.previousAction}
        previousRemarks={amend.previousRemarks}
        onClose={() => setAmend({ open: false, req: null, previousAction: null, previousRemarks: "" })}
        onDone={onActionDone}
      />
    </Layout>
  );
}

function TabBtn({ active, onClick, icon, label, count, tone, testid }) {
  const activeCls = {
    maroon: "border-[#7a1f2b] text-[#7a1f2b]",
    teal:   "border-[#2d6b6b] text-[#2d6b6b]",
    gold:   "border-[#b48a45] text-[#b48a45]",
  }[tone];
  const badge = {
    maroon: "bg-[#7a1f2b] text-white",
    teal:   "bg-[#2d6b6b] text-white",
    gold:   "bg-[#b48a45] text-white",
  }[tone];
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-2 px-4 py-2.5 border-b-2 whitespace-nowrap text-sm font-medium transition-colors ${
        active ? `${activeCls}` : "border-transparent text-[#6e7770] hover:text-[#1f2320]"
      }`}
    >
      {icon}
      {label}
      {count > 0 && (
        <span className={`ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
          active ? badge : "bg-[#e2d7c3] text-[#4a524c]"
        }`}>{count}</span>
      )}
    </button>
  );
}

function HodCard({ req, tab, userId, superView, openModal, openAmend }) {
  const r = req;
  const chip = tatChip(r.tat_deadline, r.status, r.current_stage);
  const mine = myLastAction(r, userId);
  const canAct = tab === "pending" && ((r.current_approvers || []).includes(userId) || superView) &&
                 !["completed", "rejected"].includes(r.status);
  // Phase 3d — amend prior decision. Available when the requisition is
  // currently on_hold or rejected AND the last reject/on_hold action was by
  // this user (or the viewer is super_admin).
  const lastTerminal = (r.audit_trail || []).slice().reverse().find(
    (a) => a.action === "reject" || a.action === "on_hold"
  );
  const canAmend =
    ["on_hold", "rejected"].includes(r.current_stage) &&
    !!lastTerminal &&
    (superView || lastTerminal.actor_id === userId);

  return (
    <div className={`sheet p-5 border-l-4 ${canAct ? "border-l-[#7a1f2b]" : "border-l-[#e2d7c3]"} hover-lift`}
         data-testid={`hod-card-${r.ref_code}`}>
      {canAct && (
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1" data-testid={`hod-quick-${r.ref_code}`}>
            <button className="p-2 rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-800"
                    onClick={() => openModal("approve")}
                    title="Approve" data-testid={`hod-quick-approve-${r.ref_code}`}>
              <CheckCircle2 size={16} />
            </button>
            <button className="p-2 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-800"
                    onClick={() => openModal("on_hold")}
                    title="On hold" data-testid={`hod-quick-hold-${r.ref_code}`}>
              <PauseCircle size={16} />
            </button>
            <button className="p-2 rounded-full bg-[#fcebe9] hover:bg-[#f4d6d2] text-[#b3261e]"
                    onClick={() => openModal("reject")}
                    title="Reject" data-testid={`hod-quick-reject-${r.ref_code}`}>
              <XCircle size={16} />
            </button>
          </div>
          <div className="text-xs text-[#6e7770] flex items-center gap-1">
            <Clock size={12} /> {ageWorkingHours(r.submitted_at || r.created_at)}h ago
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-start gap-2 mb-2">
        <Link to={`/requisitions/${r.ref_code}`}
              className="font-mono font-semibold text-[#7a1f2b] hover:underline">
          {r.ref_code}
        </Link>
        <span className="inline-block px-2 py-0.5 rounded-full bg-[#f4e9eb] text-[#7a1f2b] text-xs font-medium">
          {FORM_TYPE_LABEL[r.form_type] || r.form_type}
        </span>
        <StatusBadge status={r.status} formType={r.form_type} />
        {r.is_emergency && <span className="badge bg-[#b3261e] text-white border-[#b3261e]" data-testid={`hod-emergency-${r.ref_code}`}>🚨 EMERGENCY</span>}
        {r.late_submission && <span className="badge bg-[#fcebe9] text-[#b3261e] border-[#b3261e]/40">LATE</span>}
        {chip && <span className={`badge ${chip.tone}`} data-testid={`hod-tat-${r.ref_code}`}>{chip.text}</span>}
        {superView && (
          <span className="badge bg-slate-100 text-slate-700 border-slate-300" data-testid={`hod-assigned-${r.ref_code}`}>
            Assigned to: {r.hod_name || "—"}
          </span>
        )}
        {tab !== "pending" && mine && ACTION_BADGE[mine.action] && (
          <span className={`badge ${ACTION_BADGE[mine.action].tone}`} data-testid={`hod-my-action-${r.ref_code}`}>
            {ACTION_BADGE[mine.action].text} · {new Date(mine.at).toLocaleDateString()}
          </span>
        )}
      </div>

      {r.tat_deadline && canAct && (
        <div className="mb-1 text-[11px] uppercase tracking-widest font-semibold flex items-center gap-1"
             data-testid={`hod-tat-line-${r.ref_code}`}>
          <Clock size={11} /> {tatLine(r.tat_deadline)}
        </div>
      )}

      <div className="text-sm text-[#1f2320] mb-1 flex items-center gap-2 flex-wrap">
        <UserIcon size={12} className="text-[#2d6b6b]" />
        <span className="font-medium">{r.applicant_name}</span>
        <Building size={12} className="text-[#2d6b6b]" />
        <span className="text-[#4a524c]">{r.form_data?.mainDept} / {r.form_data?.subDept}</span>
      </div>
      <div className="text-sm text-[#4a524c] mb-1 line-clamp-2">{summaryLine(r) || "—"}</div>

      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
        {canAmend ? (
          <button
            onClick={() => openAmend?.(lastTerminal)}
            className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded border border-[#7a1f2b] text-[#7a1f2b] hover:bg-[#f4e9eb] font-medium"
            data-testid={`hod-amend-${r.ref_code}`}
            title="Change your previous reject / on-hold decision"
          >
            Change decision
          </button>
        ) : <span />}
        <Link to={`/requisitions/${r.ref_code}`} className="link-vgws text-xs inline-flex items-center">
          View details →
        </Link>
      </div>
    </div>
  );
}
